import { useTranslation } from "react-i18next";

import { Icon, Message } from "simplydo/react/ui";

import { UserChip } from "components/lib/Chips";
import type { ChipUser } from "components/lib/Chips/types";

import type { ChoosableUser } from "../../types";
import type { ExtraContentFunctionProps } from "../UserChooser";

const isChipUser = (user: ChoosableUser): user is ChipUser =>
  !("isEmailInvitee" in user && user.isEmailInvitee);

export const ChallengeAudienceWarning = ({
  extraContentProps,
  externalInvitesInAudience,
  children,
}: {
  extraContentProps: ExtraContentFunctionProps;
  externalInvitesInAudience: boolean;
  children: React.ReactNode;
}) => {
  const cannotViewChosenUsers = extraContentProps.users.filter(
    (c) => !(c.isInAudience || (c.isEmailInvitee && externalInvitesInAudience)),
  );

  if (cannotViewChosenUsers?.length > 0) {
    return (
      <Message warning icon>
        <Icon name="exclamation triangle" color="orange" />
        <Message.Content>
          {children}
          {cannotViewChosenUsers.map((c) => (
            <div key={c._id} style={{ display: "inline-block", margin: 10 }}>
              {isChipUser(c) ?
                <UserChip
                  user={c}
                  actionIcon="remove"
                  actionClick={() => extraContentProps.unpickUser(c._id)}
                />
              : <span style={{ display: "inline-flex", alignItems: "center" }}>
                  <Icon name="mail" /> {c.profile.fullName}
                </span>
              }
            </div>
          ))}
        </Message.Content>
      </Message>
    );
  }
  return null;
};

const AssigneeAudienceWarning = ({ challengeId }: { challengeId: string }) => {
  const { t } = useTranslation();

  return (
    <span>
      The users below are currently outside of the {t("generic.challenge")}{" "}
      audience. Users not in the {t("generic.challenge")} audience will be
      unable to view the {t("generic.challenge")} and their {t("generic.ideas")}
      . To ensure assignees can access their {t("generic.ideas")}, add them to
      the{" "}
      <a
        target="_blank"
        rel="noreferrer"
        href={`/challenges/${challengeId}/settings/audience`}
      >
        {t("generic.challenge")} audience
      </a>{" "}
      after making this assignment.
    </span>
  );
};
ChallengeAudienceWarning.AssigneeAudienceWarning = AssigneeAudienceWarning;

export const AssessorAudienceWarning = () => {
  const { t } = useTranslation();

  return (
    <Message info>
      <Message.Header>
        <Icon name="users" /> Ensuring assessor access
      </Message.Header>
      <Message.Content>
        Assigning assessors will create a new assessor group and add it to the{" "}
        {t("generic.challenge")} audience.
        <br />
        Assessors will be added to the group privately, meaning they will not be
        able to see or interact with each other.
      </Message.Content>
    </Message>
  );
};
